import { OverflowTooltip, Table } from "@4uhub/lib4uhub";
import { Box, Chip } from "@mui/material";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import MainApi from "../../../../../../services/mainApi.service";
import AutomationReceivedMessages from "./AutomationReceivedMessagesForm/AutomationReceivedMessages";
import { IAutomationReceivedMessagesPageProps } from "./models";
import { ITicketAutomationReceivedMessages } from "../../../../../../models/automation-received-message";

const TICKET_AUTOMATION_RECEIVED_MESSAGE_ROUTE =
  process.env.REACT_APP_AUTOMATIONS +
  "/api/v1/TicketAutomationReceivedMessage/";
const ticketAutomationReceivedMessageService =
  new MainApi<ITicketAutomationReceivedMessages>(
    TICKET_AUTOMATION_RECEIVED_MESSAGE_ROUTE,
  );

const translationPath =
  "chatBot.page.automations.automationReceivedRules.automationReceivedMessages.";

function joinExternalNames(
  items?: Array<{ externalName?: string | null }> | null,
): string {
  if (!items?.length) return "-";
  const seen = new Set<string>();
  const out: string[] = [];
  for (const it of items) {
    const name = (it?.externalName ?? "").trim();
    if (!name) continue;
    const key = name.toLocaleLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name);
  }
  return out.length ? out.join("; ") : "-";
}

export const AutomationReceivedMessagesPage = memo(
  ({ automationCode }: IAutomationReceivedMessagesPageProps) => {
    const [timeRuleForm, setTimeRuleForm] = useState(false);

    const { id: ticketAutomationReceivedRuleId } = useParams();

    const [selectedId, setSelectedId] = useState<string>();

    const [hasMultipleRecords, setHasMultipleRecords] = useState(false);

    const { t } = useTranslation();

    const columns: GridColDef<ITicketAutomationReceivedMessages>[] = [
      {
        field: "ticketAutomationReceivedMessageCaleClassifications",
        headerName:
          t(translationPath + "calendar_classification") ||
          "Agenda Classifications",
        flex: 1,
        renderCell: (params) => {
          const text = joinExternalNames(params.value);
          return <OverflowTooltip text={text} tooltip={text} />;
        },
      },
      {
        field: "ticketAutomationReceivedMessageCalePreferences",
        headerName: t(translationPath + "preference") || "Preferences",
        flex: 1,
        renderCell: (params) => {
          const text = joinExternalNames(params.value);
          return <OverflowTooltip text={text} tooltip={text} />;
        },
      },
      {
        field: "ticketAutomationReceivedMessageCaleGroupings",
        headerName:
          t(translationPath + "consultation_grouping_register") ||
          "Agenda Groupings",
        flex: 1,
        renderCell: (params) => {
          const text = joinExternalNames(params.value);
          return <OverflowTooltip text={text} tooltip={text} />;
        },
      },
      {
        field: "ticketAutomationReceivedCaleSpecialties",
        headerName:
          t(translationPath + "calendar_specialties") || "Agenda Specialties",
        flex: 1,
        renderCell: (params) => {
          const text = joinExternalNames(params.value);
          return <OverflowTooltip text={text} tooltip={text} />;
        },
      },
      {
        field: "ticketAutomationReceivedMessageCalendars",
        headerName: t(translationPath + "agendas") || "Agendas",
        flex: 1,
        renderCell: (params) => {
          const text = joinExternalNames(params.value);
          return <OverflowTooltip text={text} tooltip={text} />;
        },
      },
      {
        field: "enable",
        headerName: t(translationPath + "status") || "Status",
        maxWidth: 90,
        renderCell: (params) => (
          <Chip
            size="small"
            label={
              params.value
                ? t(translationPath + "active")
                : t(translationPath + "inactive")
            }
            color={params.value ? "success" : "error"}
          />
        ),
      },
    ];

    const handleAdd = () => {
      setTimeRuleForm(true);
    };

    const handleRowClick = (
      params: GridRowParams<ITicketAutomationReceivedMessages>,
    ) => {
      if (!params.row) return;
      setTimeRuleForm(true);
      setSelectedId(params.row.id);
    };

    const handleBack = useCallback(() => {
      setSelectedId(undefined);
      setTimeRuleForm(false);
    }, []);

    const props = useMemo(() => {
      return {
        TicketAutomationReceivedRuleId: ticketAutomationReceivedRuleId,
      };
    }, [ticketAutomationReceivedRuleId]);

    const showAddButton = !(hasMultipleRecords && automationCode === "26");

    return (
      <Box sx={{ height: 600, width: "100%" }}>
        {timeRuleForm && (
          <Box
            sx={(t) => ({
              p: 1,
              px: 10,
              height: "100%",
              overflow: "auto",
              backgroundColor: t.palette.background.default,
              borderRadius: 1,
            })}
          >
            <AutomationReceivedMessages
              id={selectedId}
              ticketAutomationReceivedRuleId={ticketAutomationReceivedRuleId}
              onBackButton={handleBack}
              automationCode={automationCode}
            />
          </Box>
        )}
        {!timeRuleForm && (
          <Box sx={{ height: 600 }}>
            <Table<ITicketAutomationReceivedMessages>
              showDefaultMacroFilters={false}
              service={ticketAutomationReceivedMessageService}
              columns={columns}
              title={""}
              searchInput
              onAdd={handleAdd}
              onRowClick={handleRowClick}
              searchInputAutoFocus={false}
              showButton={showAddButton}
              serviceProps={props}
              onFetchData={(items) => {
                setHasMultipleRecords((items?.length ?? 0) >= 1);
              }}
              onFetchDelete={(deletedRow) => {
                setHasMultipleRecords((prev) => {
                  return prev && deletedRow ? false : prev;
                });
              }}
              searchInputPlaceHolder={
                t(translationPath + "search") || "Searh for a message"
              }
              addButtonLabel={
                t(translationPath + "add_message") || "Add Message"
              }
              confirmOptions={{
                title: t(translationPath + "confirm.title"),
                description: t(translationPath + "confirm.description"),
                confirmationButtonProps: {
                  variant: "contained",
                },
              }}
            />
          </Box>
        )}
      </Box>
    );
  },
);
